<template>
  <model-form
    ref="modelForm"
    :model="navigation"
    modal-type="modal"
    width="500px"
    data-test-id="navigation-form"
  >
    <div class="q-gutter-sm">
      <div class="row">
        <model-form-input
          field="title"
          class="col-12"
          label="Name"
          :rules="[val => !!val || 'Field is required']"
        />

        <div
          class="row col-12 justify-between q-col-gutter-sm"
        >
          <model-form-button-toggle
            field="redirect"
            class="q-mb-lg q-pa-none q-mt-sm q-ml-sm"
            toggle-color="primary"
            :options="[
              { label: 'Page', value: false },
              { label: 'Custom Url', value: true }
            ]"
          />
          <model-form-select
            v-if="!navigation.redirect"
            field="page"
            class="col-6"
            label="Navigate to Page"
            :options="pages.data"
            option-label="title"
            use-input
            input-style="search"
            input-debounce="300"
            clearable
            bottom-slots
            :rules="[val => !!val || 'Field is required']"
            @filter="filterPages"
          />
          <model-form-input
            v-if="navigation.redirect"
            field="redirectUrl"
            class="col-6"
            label="External URL"
            :error-message="urlValidationMessage"
            :error="!urlValid"
            :hint="urlValidationMessage"
            :hide-hint="!urlValid"
            bottom-slots
          />
          <model-form-checkbox
            v-if="navigation.redirect"
            field="openNewWindow"
            class="col-6"
            label="Open in New Window"
          />
        </div>
        <div class="row col-12 q-col-gutter-sm justify-between">
          <div class="text-h6 col-12">
            Menu
          </div>
          <q-select
            class="col-6"
            label="Show In"
            bottom-slots
            multiple
            use-chips
            map-options
            emit-value
            :model-value="showIn"
            :options="$mh.asOptions(DISPLAY_OPTIONS)"
            @update:model-value="handleAddDisplayOption"
          />
          <model-form-select
            field="category"
            class="col-6"
            label="Menu Header"
            clearable
            bottom-slots
            use-input
            new-value-mode="add"
            :options="categories"
          />
        </div>
        <div class="row col-12 item-start">
          <div class="text-h6 col-12">
            Mobile
          </div>
          <div
            class="col-6"
          >
            <model-form-checkbox
              field="showInMobileMenu"
              class="col-6"
              label="Mobile Web Tab Bar"
            />
            <model-form-checkbox
              field="showInMobileApp"
              class="col-6"
              label="Mobile App Tab Bar"
            />
          </div>
          <div
            class="row col-12"
          >
            <model-form-input
              field="mobileMenuLabel"
              class="col-6 q-mr-lg"
              label="Mobile Label"
              :disable="!navigation.showInMobileMenu && !navigation.showInMobileApp"
              :rules="[val => !!val || mobileOptionsDisabled || 'Field is required']"
              bottom-slots
            />
            <div
              class="col-2 items-center justify-center"
              style="border: 1px solid lightgray; border-radius: 5px; cursor: pointer; height: 56px;"
            >
              <div
                class="q-field__label text-caption"
                :style="{ 'color': mobileOptionsDisabled ? 'darkgray' : 'gray' }"
              >
                Mobile Icon
              </div>
              <q-btn
                :icon="navigation.mobileMenuIcon"
                size="md"
                :disabled="mobileOptionsDisabled"
                @click="$refs.iconPickerDialog.show()"
              />
            </div>
          </div>
        </div>
      </div>
      <q-btn
        :label="showPreview ? 'Hide Preview' : 'Show Preview'"
        :disable="!urlValid"
        @click="() => showPreview = !showPreview"
      >
        <q-icon :name="showPreview ? 'expand_less' : 'expand_more'" />
      </q-btn>
      <iframe
        v-if="$q.screen.gt.sm && urlValid && showPreview"
        style="width: 100%; aspect-ratio: 3 / 2"
        :src="previewUrl"
      />
    </div>
    <icon-picker-dialog
      ref="iconPickerDialog"
      :model="navigation"
      icon-property="mobileMenuIcon"
    />
  </model-form>
</template>

<script>
import { DISPLAY_OPTIONS } from 'src/models/page/Navigation'
import IconPickerDialog from 'src/modules/admin/components/IconPickerDialog'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'

export default {
  name: 'NaviationForm',
  components: {
    ModelForm,
    IconPickerDialog
  },
  configuration: ['currentSite'],
  data () {
    return {
      urlValidationMessage: null,
      urlValid: true,
      showPreview: false,
      pageSearchString: null
    }
  },
  models: {
    navigation: {
      content: true,
      page: {
        fields: ['id', 'title', 'slug']
      }
    },
    navigations: {
      fields: ['id', 'category'],
      variables: {
        limit: 100
      }
    },
    pages: {
      fields: ['id', 'title'],
      variables () {
        return {
          searchString: this.pageSearchString,
          limit: 10
        }
      }
    }
  },
  computed: {
    categories () {
      return _(this.navigations.data).map('category').uniq().compact().value()
    },
    previewUrl () {
      if (!this.urlValid) return ''

      if (!this.navigation.redirect && this.navigation.page) {
        return this.currentSite.url(this.navigation.page.slug)
      } else if (!this.navigation.redirect) {
        return ''
      }

      if (_.startsWith(this.navigation.redirectUrl, '/')) {
        return this.currentSite.url(this.navigation.redirectUrl)
      } else {
        return this.navigation.redirectUrl
      }
    },
    showIn () {
      const showIn = []
      if (this.navigation.showInHeader) showIn.push(this.DISPLAY_OPTIONS.HEADER)
      if (this.navigation.showInFooter) showIn.push(this.DISPLAY_OPTIONS.FOOTER)
      if (this.navigation.showInMenu) showIn.push(this.DISPLAY_OPTIONS.MENU)
      return showIn
    },
    mobileOptionsDisabled () {
      return !this.navigation.showInMobileMenu && !this.navigation.showInMobileApp
    }
  },
  watch: {
    'navigation.redirectUrl' () {
      this.validateUrl()
    },
    'navigation.page' () {
      this.validateUrl()
    }
  },
  created () {
    _.extend(this, {
      DISPLAY_OPTIONS
    })
  },
  methods: {
    show (model, initialData) {
      this.$refs.modelForm.show(model, initialData)
    },
    validateUrl () {
      if (!this.navigation.redirect) {
        this.urlValid = this.navigation.page
        this.urlValidationMessage = null
      } else {
        const result = this.validateCustomUrl(this.navigation.redirectUrl)

        this.urlValid = result.valid
        this.urlValidationMessage = result.warning
      }
    },
    validateCustomUrl (url) {
      const hostname = this.currentSite.hostname
      const urlContainsHostname = _.includes(url, hostname.replace('https://', ''))
      const urlCheck = { valid: true, warning: null }
      const urlErrorMessage = this.$mh.foundNetlifyUrl(url)
      if (url === null || url === '') {
        urlCheck.warning = 'Url must not be empty'
        urlCheck.valid = false
      } else if (_.startsWith(url, 'http:')) {
        urlCheck.warning = 'Url is insecure, use https for secure links'
        urlCheck.valid = false
      } else if (_.startsWith(url, '/')) {
        urlCheck.warning = `Current Url starts with "/" which will map to an internal page ${hostname}${url}`
      } else if (!_.includes(url, '://')) {
        urlCheck.warning = 'Url is missing protocol, e.g. https:// or sftp://'
      } else if (urlContainsHostname) {
        urlCheck.warning = `Url contains hostname, remove ${this.currentSite.hostname} to continue`
        urlCheck.valid = false
      } else if (urlErrorMessage) {
        urlCheck.warning = `Url contains ${urlErrorMessage}`
      }

      return urlCheck
    },
    handleAddDisplayOption (value) {
      this.navigation.showInHeader = value.includes(this.DISPLAY_OPTIONS.HEADER)
      this.navigation.showInFooter = value.includes(this.DISPLAY_OPTIONS.FOOTER)
      this.navigation.showInMenu = value.includes(this.DISPLAY_OPTIONS.MENU)
    },
    filterPages (val, update) {
      update(() => {
        this.pageSearchString = _.isEmpty(val) ? null : val.trim()
      })
    }
  }
}
</script>
